// Lightweight document detection + perspective correction (no external deps).


export const isGoodQuad = (r) => r.found && r.fill > 0.55 && r.areaFrac < 0.95 && r.areaFrac > 0.12;

// Find the document on a captured full-resolution frame (finer grid than the live preview).
export function detectOnCanvas(canvas) {
  const ws = createWorkspace();
  const r = analyzeFrame(canvas, ws, 480);
  return isGoodQuad(r) ? r.quad : null;
}


export function createWorkspace() {
  const canvas = document.createElement("canvas");
  return { canvas, ctx: canvas.getContext("2d", { willReadFrequently: true }) };
}

function polyArea(q) {
  let a = 0;
  for (let i = 0; i < q.length; i++) { const p = q[i], r = q[(i + 1) % q.length]; a += p[0] * r[1] - r[0] * p[1]; }
  return Math.abs(a) / 2;
}

// Order corners TL, TR, BR, BL (clockwise on screen, starting nearest the top-left).
function orderQuad(q) {
  const cx = (q[0][0] + q[1][0] + q[2][0] + q[3][0]) / 4, cy = (q[0][1] + q[1][1] + q[2][1] + q[3][1]) / 4;
  const s = q.slice().sort((a, b) => Math.atan2(a[1] - cy, a[0] - cx) - Math.atan2(b[1] - cy, b[0] - cx));
  let k = 0;
  for (let i = 1; i < 4; i++) if (s[i][0] + s[i][1] < s[k][0] + s[k][1]) k = i;
  return [s[k], s[(k + 1) % 4], s[(k + 2) % 4], s[(k + 3) % 4]];
}

function cornerAngles(q) {
  return q.map((p, i) => {
    const a = q[(i + 3) % 4], b = q[(i + 1) % 4];
    const v1 = [a[0] - p[0], a[1] - p[1]], v2 = [b[0] - p[0], b[1] - p[1]];
    const dot = v1[0] * v2[0] + v1[1] * v2[1], m = Math.hypot(...v1) * Math.hypot(...v2) || 1e-9;
    return (Math.acos(Math.max(-1, Math.min(1, dot / m))) * 180) / Math.PI;
  });
}

// Sobel gradient → { mag (0..255-ish), thin (non-max-suppressed edge mask), thick (looser edge mask), gx, gy }
function edges(gray, W, H) {
  const n = W * H, mag = new Uint8Array(n), gx = new Int16Array(n), gy = new Int16Array(n), hist = new Uint32Array(256);
  for (let y = 1; y < H - 1; y++) for (let x = 1; x < W - 1; x++) {
    const p = y * W + x;
    const sx = gray[p - W + 1] + 2 * gray[p + 1] + gray[p + W + 1] - gray[p - W - 1] - 2 * gray[p - 1] - gray[p + W - 1];
    const sy = gray[p + W - 1] + 2 * gray[p + W] + gray[p + W + 1] - gray[p - W - 1] - 2 * gray[p - W] - gray[p - W + 1];
    gx[p] = sx; gy[p] = sy;
    const m = Math.min(255, (Math.abs(sx) + Math.abs(sy)) >> 2);
    mag[p] = m; hist[m]++;
  }
  let acc = 0, thr = 12;
  for (let i = 255; i >= 0; i--) { acc += hist[i]; if (acc > n * 0.08) { thr = Math.max(12, Math.min(40, i)); break; } }
  const thin = new Uint8Array(n), thick = new Uint8Array(n), low = Math.max(8, thr * 0.6);
  for (let y = 1; y < H - 1; y++) for (let x = 1; x < W - 1; x++) {
    const p = y * W + x, m = mag[p];
    if (m >= low) thick[p] = 1;
    if (m < thr) continue;
    const horiz = Math.abs(gx[p]) >= Math.abs(gy[p]);
    if (horiz ? m >= mag[p - 1] && m >= mag[p + 1] : m >= mag[p - W] && m >= mag[p + W]) thin[p] = 1;
  }
  return { mag, thin, thick, gx, gy };
}

// Gradient-guided Hough transform → strongest straight lines {theta (rad), rho, votes}.
function houghLines(e, W, H, maxLines) {
  const T = 180, diag = Math.ceil(Math.hypot(W, H)), R = 2 * diag + 1;
  const acc = new Uint32Array(T * R);
  const cs = new Float32Array(T), sn = new Float32Array(T);
  for (let t = 0; t < T; t++) { cs[t] = Math.cos((t * Math.PI) / 180); sn[t] = Math.sin((t * Math.PI) / 180); }
  for (let y = 1; y < H - 1; y++) for (let x = 1; x < W - 1; x++) {
    const p = y * W + x;
    if (!e.thin[p]) continue;
    const w = Math.min(64, e.mag[p]); // gradient-weighted: a crisp paper edge outranks long faint texture lines
    let a = Math.round((Math.atan2(e.gy[p], e.gx[p]) * 180) / Math.PI);
    if (a < 0) a += 180; if (a >= 180) a -= 180;
    for (let dt = -8; dt <= 8; dt++) {
      let t = a + dt; if (t < 0) t += 180; if (t >= 180) t -= 180;
      const rho = Math.round(x * cs[t] + y * sn[t]) + diag;
      acc[t * R + rho] += w;
    }
  }
  const minVotes = Math.max(20, 0.12 * Math.min(W, H)) * 12;
  const peaks = [];
  for (let t = 0; t < T; t++) for (let r = 0; r < R; r++) {
    const v = acc[t * R + r];
    if (v < minVotes) continue;
    let isMax = true;
    for (let dt = -4; dt <= 4 && isMax; dt++) for (let dr = -4; dr <= 4; dr++) {
      let tt = t + dt, rr = r + dr;
      if (tt < 0 || tt >= T) { tt = (tt + T) % T; rr = 2 * diag - rr; }
      if (rr < 0 || rr >= R) continue;
      const w = acc[tt * R + rr];
      if (w > v || (w === v && (dt < 0 || (dt === 0 && dr < 0)))) { isMax = false; break; }
    }
    if (isMax) peaks.push({ t, r, votes: v });
  }
  peaks.sort((a, b) => b.votes - a.votes);
  const lines = [];
  for (const p of peaks) {
    if (lines.length >= maxLines) break;
    const rho = p.r - diag;
    const dup = lines.some((l) => {
      const dt = Math.min(Math.abs(l.t - p.t), 180 - Math.abs(l.t - p.t));
      const r2 = Math.abs(l.t - p.t) > 90 ? -l.rho : l.rho;
      return dt <= 6 && Math.abs(r2 - rho) <= Math.max(6, 0.03 * Math.min(W, H));
    });
    // Cap near-parallel lines so a striped table cannot crowd out the sheet's other edges.
    const similar = lines.filter((l) => Math.min(Math.abs(l.t - p.t), 180 - Math.abs(l.t - p.t)) <= 15).length;
    if (!dup && similar < 14) lines.push({ t: p.t, theta: (p.t * Math.PI) / 180, rho, votes: p.votes, border: false });
  }
  return lines;
}

function intersect(a, b) {
  const c1 = Math.cos(a.theta), s1 = Math.sin(a.theta), c2 = Math.cos(b.theta), s2 = Math.sin(b.theta);
  const det = c1 * s2 - c2 * s1;
  if (Math.abs(det) < 1e-6) return null;
  return [(a.rho * s2 - b.rho * s1) / det, (b.rho * c1 - a.rho * c2) / det];
}

const angleDiff = (a, b) => { const d = Math.abs(a - b) % 180; return Math.min(d, 180 - d); };

// Score one side between corners a→b: edge support (fraction of samples on an edge) and brightness contrast
// (inside − outside, checked at two depths so a text row next to a ruling line does not look like a paper edge).
function sideScore(gray, thick, W, H, a, b, cx, cy) {
  const N = 24, d = Math.max(3, Math.round(W / 120));
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
  let nx = -(b[1] - a[1]) / len, ny = (b[0] - a[0]) / len;
  const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
  if ((cx - mx) * nx + (cy - my) * ny < 0) { nx = -nx; ny = -ny; }
  const at = (x, y) => { const ix = Math.round(x), iy = Math.round(y); return ix < 0 || iy < 0 || ix >= W || iy >= H ? -1 : gray[iy * W + ix]; };
  let sup = 0, con = 0, valid = 0;
  for (let k = 0; k < N; k++) {
    const t = (k + 0.5) / N, px = a[0] + (b[0] - a[0]) * t, py = a[1] + (b[1] - a[1]) * t;
    const gin = at(px + nx * d, py + ny * d), g1 = at(px - nx * d, py - ny * d), g2 = at(px - nx * 2 * d, py - ny * 2 * d);
    if (gin < 0 || g1 < 0 || g2 < 0) continue;
    valid++;
    con += Math.min(gin - g1, gin - g2);
    for (let j = -2; j <= 2; j++) {
      const ex = Math.round(px + nx * j), ey = Math.round(py + ny * j);
      if (ex >= 0 && ey >= 0 && ex < W && ey < H && thick[ey * W + ex]) { sup++; break; }
    }
  }
  if (valid < N * 0.6) return null;
  return { support: sup / valid, contrast: con / valid };
}

// Find the best paper quadrilateral from straight edges (robust to reflective tables, shadows and nearby sheets).
function findQuad(gray, W, H) {
  const e = edges(gray, W, H);
  const lines = houghLines(e, W, H, 28);
  // Picture borders as fallback sides (sheet partially out of frame) — never scored, max two per quad.
  lines.push({ theta: 0, rho: 0.5, border: true }, { theta: 0, rho: W - 0.5, border: true }, { theta: Math.PI / 2, rho: 0.5, border: true }, { theta: Math.PI / 2, rho: H - 0.5, border: true });
  const deg = (l) => (l.theta * 180) / Math.PI;
  const meanAngle = (a, b) => { const ra = (a * Math.PI) / 90, rb = (b * Math.PI) / 90; return ((Math.atan2(Math.sin(ra) + Math.sin(rb), Math.cos(ra) + Math.cos(rb)) / 2) * 180) / Math.PI; };
  const minDim = Math.min(W, H), n = W * H;
  const pairs = [];
  for (let i = 0; i < lines.length; i++) for (let j = i + 1; j < lines.length; j++) {
    const A = lines[i], B = lines[j];
    if (A.border && B.border && Math.abs(A.theta - B.theta) > 0.01) continue;
    if (angleDiff(deg(A), deg(B)) > 35) continue;
    const rB = Math.abs(deg(A) - deg(B)) > 90 ? -B.rho : B.rho;
    if (Math.abs(A.rho - rB) < 0.15 * minDim) continue;
    pairs.push([A, B, meanAngle(deg(A), deg(B))]);
  }
  // Pass 0: quads made only of detected edges. Pass 1 (only if nothing found): allow up to two picture borders.
  let best = null;
  for (let pass = 0; pass < 2 && !best; pass++) for (let i = 0; i < pairs.length; i++) for (let j = i + 1; j < pairs.length; j++) {
    const [A, B, angP] = pairs[i], [C, D, angQ] = pairs[j];
    const ad = angleDiff(angP, angQ);
    if (ad < 55 || ad > 125) continue;
    if (A === C || A === D || B === C || B === D) continue;
    const borders = [A, B, C, D].filter((l) => l.border).length;
    if (borders > 2 || (pass === 0 && borders)) continue;
    const c = [intersect(A, C), intersect(C, B), intersect(B, D), intersect(D, A)];
    if (c.some((p) => !p || p[0] < -0.03 * W || p[0] > 1.03 * W || p[1] < -0.03 * H || p[1] > 1.03 * H)) continue;
    const q = orderQuad(c);
    const area = polyArea(q), areaFrac = area / n;
    if (areaFrac < 0.1 || areaFrac > 0.96) continue;
    if (cornerAngles(q).some((x) => x < 55 || x > 125)) continue;
    const wAvg = (Math.hypot(q[1][0] - q[0][0], q[1][1] - q[0][1]) + Math.hypot(q[2][0] - q[3][0], q[2][1] - q[3][1])) / 2;
    const hAvg = (Math.hypot(q[3][0] - q[0][0], q[3][1] - q[0][1]) + Math.hypot(q[2][0] - q[1][0], q[2][1] - q[1][1])) / 2;
    const ratio = wAvg / (hAvg || 1);
    if (ratio < 0.3 || ratio > 3.3) continue;
    const cx = (q[0][0] + q[1][0] + q[2][0] + q[3][0]) / 4, cy = (q[0][1] + q[1][1] + q[2][1] + q[3][1]) / 4;
    // Side k of the ordered quad lies on a border line iff its midpoint sits on the frame edge.
    // Border sides cannot be measured: they get a neutral, slightly-below-good score so a real edge always wins.
    let supSum = 0, conSum = 0, minSup = 1, minCon = Infinity, ok = true, borderSides = 0;
    for (let k = 0; k < 4 && ok; k++) {
      const a = q[k], b = q[(k + 1) % 4], mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
      let s;
      if (mx < 1.5 || mx > W - 1.5 || my < 1.5 || my > H - 1.5) { borderSides++; s = { support: 0.8, contrast: 45 }; }
      else { s = sideScore(gray, e.thick, W, H, a, b, cx, cy); if (!s) { ok = false; break; } if (s.support < 0.4 || s.contrast < 6) ok = false; }
      supSum += s.support; conSum += s.contrast;
      minSup = Math.min(minSup, s.support); minCon = Math.min(minCon, s.contrast);
    }
    if (!ok || borderSides > 2) continue;
    const meanSup = supSum / 4, meanCon = conSum / 4;
    if (meanSup < 0.6 || meanCon < 15) continue;
    const score = minSup + Math.min(1, minCon / 80) + 0.5 * meanSup + 0.3 * areaFrac;
    if (!best || score > best.score) best = { q, score, areaFrac, fill: meanSup, touchesBorder: borderSides > 0 || q.some((p) => p[0] < 0.01 * W || p[0] > 0.99 * W || p[1] < 0.01 * H || p[1] > 0.99 * H) };
  }
  return best;
}

// Analyse current frame (video element or canvas). Returns detection result with normalised quad (0..1).
export function analyzeFrame(video, ws, W = 240) {
  const vw = video.videoWidth || video.width, vh = video.videoHeight || video.height;
  if (!vw || !vh) return { found: false };
  const H = Math.round((vh / vw) * W);
  ws.canvas.width = W; ws.canvas.height = H;
  ws.ctx.drawImage(video, 0, 0, W, H);
  const { data } = ws.ctx.getImageData(0, 0, W, H);
  const n = W * H;
  const gray = new Uint8Array(n);
  for (let i = 0, j = 0; i < n; i++, j += 4) gray[i] = (data[j] * 299 + data[j + 1] * 587 + data[j + 2] * 114) / 1000;
  const r = findQuad(gray, W, H);
  if (!r) return { found: false, sharpness: 0 };

  const xs = r.q.map((p) => p[0]), ys = r.q.map((p) => p[1]);
  const minX = Math.max(0, Math.floor(Math.min(...xs))), maxX = Math.min(W - 1, Math.ceil(Math.max(...xs)));
  const minY = Math.max(0, Math.floor(Math.min(...ys))), maxY = Math.min(H - 1, Math.ceil(Math.max(...ys)));
  // Sharpness: mean absolute Laplacian within the document bbox
  let lap = 0, cnt = 0;
  for (let y = Math.max(1, minY); y < Math.min(H - 1, maxY); y += 2) {
    for (let x = Math.max(1, minX); x < Math.min(W - 1, maxX); x += 2) {
      const p = y * W + x;
      lap += Math.abs(4 * gray[p] - gray[p - 1] - gray[p + 1] - gray[p - W] - gray[p + W]);
      cnt++;
    }
  }
  const quad = r.q.map(([x, y]) => [Math.min(1, Math.max(0, x / W)), Math.min(1, Math.max(0, y / H))]);
  return { found: true, quad, areaFrac: r.areaFrac, fill: r.fill, touchesBorder: r.touchesBorder, sharpness: cnt ? lap / cnt : 0, bbox: [minX / W, minY / H, (maxX + 1) / W, (maxY + 1) / H] };
}

export function quadMotion(a, b) {
  if (!a || !b) return 1;
  let m = 0;
  for (let i = 0; i < 4; i++) m = Math.max(m, Math.hypot(a[i][0] - b[i][0], a[i][1] - b[i][1]));
  return m;
}

export function quadCentroid(q) {
  return [(q[0][0] + q[1][0] + q[2][0] + q[3][0]) / 4, (q[0][1] + q[1][1] + q[2][1] + q[3][1]) / 4];
}

// Solve homography mapping unit square -> quad (src points in pixels). Returns 3x3 as array.
function homography(dst) {
  const [x0, y0] = dst[0], [x1, y1] = dst[1], [x2, y2] = dst[2], [x3, y3] = dst[3];
  const dx1 = x1 - x2, dx2 = x3 - x2, dx3 = x0 - x1 + x2 - x3;
  const dy1 = y1 - y2, dy2 = y3 - y2, dy3 = y0 - y1 + y2 - y3;
  const det = dx1 * dy2 - dx2 * dy1 || 1e-9;
  const g = (dx3 * dy2 - dx2 * dy3) / det;
  const h = (dx1 * dy3 - dx3 * dy1) / det;
  const a = x1 - x0 + g * x1, b = x3 - x0 + h * x3, c = x0;
  const d = y1 - y0 + g * y1, e = y3 - y0 + h * y3, f = y0;
  return [a, b, c, d, e, f, g, h];
}

// Warp the quad region of source canvas to a flat rectangle; returns a new canvas.
// Push each corner away from the centroid by `margin` of the quad's own size so a slightly-inside corner estimate never shaves text.
function expandQuad(q, margin, sw, sh) {
  const cx = q.reduce((s, p) => s + p[0], 0) / 4, cy = q.reduce((s, p) => s + p[1], 0) / 4;
  const f = 1 + 2 * margin;
  return q.map(([x, y]) => [Math.min(sw - 1, Math.max(0, cx + (x - cx) * f)), Math.min(sh - 1, Math.max(0, cy + (y - cy) * f))]);
}

// Plain downscale (no crop) used as the fallback when no document is found.
export function resizeCanvas(src, maxSide) {
  const scale = Math.min(1, maxSide / Math.max(src.width, src.height));
  if (scale === 1) return src;
  const out = document.createElement("canvas");
  out.width = Math.round(src.width * scale); out.height = Math.round(src.height * scale);
  out.getContext("2d").drawImage(src, 0, 0, out.width, out.height);
  return out;
}

// Ink just inside each edge of the warped page. Paper margins are blank, so dark pixels there mean the crop cut through
// text — UNLESS they are background: table/sofa that intrudes from the crop edge (margin, curled or tilted paper edge).
// Scanning each line from the outer edge inward, dark pixels that run contiguously from the edge are background; dark pixels
// that appear after the first light (paper) pixel are ink. Returns the ink fraction of the band per side + the worst side.
export function borderInkSides(canvas, band = 0.02, inset = 0.015) {
  const w = canvas.width, h = canvas.height;
  const { data } = canvas.getContext("2d").getImageData(0, 0, w, h);
  const lum = (x, y) => { const i = (y * w + x) * 4; return (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000; };
  const bx = Math.max(1, Math.round(w * band)), by = Math.max(1, Math.round(h * band));
  const ix = Math.round(w * inset), iy = Math.round(h * inset);
  // "paper" = a run of clearly bright pixels; textured backgrounds (fabric, wood) have bright speckles but not long bright runs
  const gap = Math.max(4, Math.round(Math.min(w, h) * 0.008));
  const sides = {};
  // each entry: lines to scan, and for a line index `t` the pixel at depth `d` from the outer edge
  const scans = {
    top: { lines: [ix, w - ix], depth: iy + by, px: (t, d) => [t, d] },
    bottom: { lines: [ix, w - ix], depth: iy + by, px: (t, d) => [t, h - 1 - d] },
    left: { lines: [iy, h - iy], depth: ix + bx, px: (t, d) => [d, t] },
    right: { lines: [iy, h - iy], depth: ix + bx, px: (t, d) => [w - 1 - d, t] },
  };
  let worst = 0;
  for (const k in scans) {
    const { lines, depth, px } = scans[k];
    const inner = k === "top" || k === "bottom" ? iy : ix;
    let n = 0, ink = 0;
    for (let t = lines[0]; t < lines[1]; t += 2) {
      let seenPaper = false, bright = 0;
      for (let d = 0; d < depth; d++) {
        const [x, y] = px(t, d);
        const l = lum(x, y);
        bright = l > 170 ? bright + 1 : 0;
        if (bright >= gap) seenPaper = true;
        if (d >= inner) { n++; if (l < 100 && seenPaper) ink++; }
      }
    }
    sides[k] = n ? ink / n : 0;
    worst = Math.max(worst, sides[k]);
  }
  return { worst, sides };
}

export function borderInkRatio(canvas, band = 0.02, inset = 0.015) {
  return borderInkSides(canvas, band, inset).worst;
}

// Share of bright pixels in the outer band of the whole photo. A screenshot / scan / PDF render is paper right up to the
// frame edge (≈ 100 %); a photo of paper on a table is not. Such frames must never be "cropped" to an inner rectangle.
export function frameBorderBrightRatio(canvas, band = 0.04) {
  const w = canvas.width, h = canvas.height;
  const { data } = canvas.getContext("2d").getImageData(0, 0, w, h);
  const bx = Math.round(w * band), by = Math.round(h * band);
  let n = 0, b = 0;
  for (let y = 0; y < h; y += 2) for (let x = 0; x < w; x += 2) {
    if (x >= bx && x < w - bx && y >= by && y < h - by) continue;
    n++;
    const i = (y * w + x) * 4;
    if ((data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000 > 170) b++;
  }
  return n ? b / n : 0;
}

export function warpQuad(srcCanvas, quadNorm, maxSide = 1800, margin = 0) {
  const sw = srcCanvas.width, sh = srcCanvas.height;
  let q = quadNorm.map(([x, y]) => [x * sw, y * sh]);
  if (margin > 0) q = expandQuad(q, margin, sw, sh);
  const widthTop = Math.hypot(q[1][0] - q[0][0], q[1][1] - q[0][1]);
  const widthBottom = Math.hypot(q[2][0] - q[3][0], q[2][1] - q[3][1]);
  const heightL = Math.hypot(q[3][0] - q[0][0], q[3][1] - q[0][1]);
  const heightR = Math.hypot(q[2][0] - q[1][0], q[2][1] - q[1][1]);
  let ow = Math.round(Math.max(widthTop, widthBottom));
  let oh = Math.round(Math.max(heightL, heightR));
  const scale = Math.min(1, maxSide / Math.max(ow, oh));
  ow = Math.max(200, Math.round(ow * scale)); oh = Math.max(200, Math.round(oh * scale));
  const sctx = srcCanvas.getContext("2d");
  const src = sctx.getImageData(0, 0, sw, sh).data;
  const out = document.createElement("canvas");
  out.width = ow; out.height = oh;
  const octx = out.getContext("2d");
  const img = octx.createImageData(ow, oh);
  const o = img.data;
  const [a, b, c, d, e, f, g, h] = homography(q);
  for (let y = 0; y < oh; y++) {
    const v = y / (oh - 1);
    for (let x = 0; x < ow; x++) {
      const u = x / (ow - 1);
      const den = g * u + h * v + 1;
      let sx = (a * u + b * v + c) / den, sy = (d * u + e * v + f) / den;
      sx = Math.min(sw - 1.001, Math.max(0, sx)); sy = Math.min(sh - 1.001, Math.max(0, sy));
      const x0 = sx | 0, y0 = sy | 0, fx = sx - x0, fy = sy - y0;
      const i00 = (y0 * sw + x0) * 4, i10 = i00 + 4, i01 = i00 + sw * 4, i11 = i01 + 4;
      const oi = (y * ow + x) * 4;
      for (let ch = 0; ch < 3; ch++) {
        const top = src[i00 + ch] * (1 - fx) + src[i10 + ch] * fx;
        const bot = src[i01 + ch] * (1 - fx) + src[i11 + ch] * fx;
        o[oi + ch] = top * (1 - fy) + bot * fy;
      }
      o[oi + 3] = 255;
    }
  }
  octx.putImageData(img, 0, 0);
  return out;
}

// Gentle readability boost: percentile contrast stretch (keeps colour, doesn't alter content).
export function enhance(canvas) {
  const ctx = canvas.getContext("2d");
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = img.data, hist = new Uint32Array(256), n = canvas.width * canvas.height;
  for (let i = 0; i < d.length; i += 4) hist[((d[i] * 299 + d[i + 1] * 587 + d[i + 2] * 114) / 1000) | 0]++;
  let lo = 0, hi = 255, acc = 0;
  for (let i = 0; i < 256; i++) { acc += hist[i]; if (acc > n * 0.01) { lo = i; break; } }
  acc = 0;
  for (let i = 255; i >= 0; i--) { acc += hist[i]; if (acc > n * 0.01) { hi = i; break; } }
  if (hi - lo < 40) return canvas;
  const scale = 255 / (hi - lo);
  const lut = new Uint8ClampedArray(256);
  for (let i = 0; i < 256; i++) lut[i] = (i - lo) * scale;
  for (let i = 0; i < d.length; i += 4) { d[i] = lut[d[i]]; d[i + 1] = lut[d[i + 1]]; d[i + 2] = lut[d[i + 2]]; }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

// Higher-resolution sharpness check on the document region (used right before auto capture).
export function measureSharpness(video, bbox, ws) {
  const vw = video.videoWidth, vh = video.videoHeight;
  if (!vw || !vh) return 0;
  const [x0, y0, x1, y1] = bbox || [0, 0, 1, 1];
  const sx = x0 * vw, sy = y0 * vh, sw = Math.max(1, (x1 - x0) * vw), sh = Math.max(1, (y1 - y0) * vh);
  const W = 640, H = Math.max(8, Math.round((sh / sw) * W));
  ws.canvas.width = W; ws.canvas.height = H;
  ws.ctx.drawImage(video, sx, sy, sw, sh, 0, 0, W, H);
  const { data } = ws.ctx.getImageData(0, 0, W, H);
  const n = W * H, g = new Uint8Array(n);
  for (let i = 0, j = 0; i < n; i++, j += 4) g[i] = (data[j] * 299 + data[j + 1] * 587 + data[j + 2] * 114) / 1000;
  let acc = 0, cnt = 0;
  for (let y = 2; y < H - 2; y += 2) for (let x = 2; x < W - 2; x += 2) {
    const p = y * W + x;
    acc += Math.abs(4 * g[p] - g[p - 1] - g[p + 1] - g[p - W] - g[p + W]); cnt++;
  }
  return cnt ? acc / cnt : 0;
}

export function canvasSharpness(canvas) {
  const w = canvas.width, h = canvas.height, s = Math.min(1, 800 / w);
  const c = document.createElement("canvas"); c.width = Math.round(w * s); c.height = Math.round(h * s);
  const ctx = c.getContext("2d"); ctx.drawImage(canvas, 0, 0, c.width, c.height);
  const { data } = ctx.getImageData(0, 0, c.width, c.height);
  const W = c.width, n = W * c.height, g = new Uint8Array(n);
  for (let i = 0, j = 0; i < n; i++, j += 4) g[i] = (data[j] * 299 + data[j + 1] * 587 + data[j + 2] * 114) / 1000;
  let acc = 0, cnt = 0;
  for (let y = 2; y < c.height - 2; y += 2) for (let x = 2; x < W - 2; x += 2) {
    const p = y * W + x; acc += Math.abs(4 * g[p] - g[p - 1] - g[p + 1] - g[p - W] - g[p + W]); cnt++;
  }
  return cnt ? acc / cnt : 0;
}

export function canvasToBlob(canvas, quality = 0.92) {
  return new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
}
